// Vues de la revue opérationnelle : état global, matrice santé × pilotabilité, tableau par entité.
// Logique pure, utilisée par le front.

import { addDays } from '../format.js';
import { OPS_PRM, addPostes, etatOf, margOf, rateOf, zeroPostes, type OpsParams } from './compute.js';
import type { OpsChantier, OpsDataset, OpsEtat, OpsHealth } from './types.js';

export const OPS_CELLS = ['r-p', 'a-p', 'r-f', 'a-f', 'g-p', 'g-f'] as const;
export type OpsCell = (typeof OPS_CELLS)[number];

export const ETAT_LABEL: Record<OpsEtat, string> = { r: 'En dérive', a: 'À surveiller', g: 'Bons' };
export const ETAT_HEALTH: Record<OpsEtat, OpsHealth> = { r: 'crit', a: 'surv', g: 'bon' };
export const CELL_LABEL: Record<OpsCell, string> = {
  'r-p': 'à travailler maintenant',
  'r-f': 'à constater, retour d’expérience',
  'a-p': 'à travailler maintenant',
  'a-f': 'à régler avant clôture',
  'g-p': 'à laisser avancer',
  'g-f': 'à clôturer',
};

export const cellOf = (r: OpsChantier): OpsCell => `${r.c.e}-${r.c.p ? 'p' : 'f'}` as OpsCell;

export interface OpsFilters {
  /** Entité Flex, 'Tous' pour toutes. */
  ent: string;
  /** Responsable : « c|Nom » pour un conducteur, « m|Nom » pour un commercial, '' pour tous. */
  who: string;
  /** Recherche libre sur le nom, le client et le code. */
  q: string;
}
export const DEFAULT_OPS_FILTERS: OpsFilters = { ent: 'Tous', who: '', q: '' };

const norm = (s: string | null | undefined) => (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function parseWho(who: string): { k: 'c' | 'm'; n: string } | null {
  if (!who) return null;
  const i = who.indexOf('|');
  return { k: who.slice(0, i) === 'm' ? 'm' : 'c', n: who.slice(i + 1) };
}

export function filterOps(rows: OpsChantier[], f: OpsFilters): OpsChantier[] {
  const w = parseWho(f.who);
  const q = norm(f.q).trim();
  return rows.filter(
    (r) =>
      (f.ent === 'Tous' || r.ent === f.ent) &&
      (!w || (w.k === 'c' ? r.cond === w.n : r.com === w.n)) &&
      (!q || norm(`${r.nom ?? ''} ${r.cli ?? ''} ${r.code}`).includes(q)),
  );
}

/** Responsables du périmètre, avec le nombre de chantiers : conducteurs d'abord, puis commerciaux sans conducteur. */
export function opsPeople(rows: OpsChantier[]): { value: string; label: string; n: number }[] {
  const c = new Map<string, number>();
  const m = new Map<string, number>();
  for (const r of rows) {
    if (r.cond) c.set(r.cond, (c.get(r.cond) ?? 0) + 1);
    else if (r.com) m.set(r.com, (m.get(r.com) ?? 0) + 1);
  }
  const out = (src: Map<string, number>, k: 'c' | 'm', suffix: string) =>
    [...src.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
      .map(([n, v]) => ({ value: `${k}|${n}`, label: `${n}${suffix}`, n: v }));
  return [...out(c, 'c', ''), ...out(m, 'm', ' (commercial)')];
}

export interface OpsSummary {
  n: number;
  /** Chantiers pilotables (facturés à moins de 90 %). */
  nP: number;
  /** En dérive, dont pilotables. */
  nR: number;
  nRp: number;
  /** Taux de marge prévu et à terminaison, en %. */
  tauxP: number | null;
  tauxT: number | null;
  /** Écart de taux, en points. */
  dG: number;
  etat: OpsHealth;
  /** Marge prévue et à terminaison en euros, reste à facturer. */
  margeP: number;
  margeT: number;
  raf: number;
  ca: number;
}

export function summarize(rows: OpsChantier[], prm: OpsParams = OPS_PRM): OpsSummary {
  const n = rows.length;
  const P = rows.reduce((a, r) => addPostes(a, r.Pv), zeroPostes());
  const T = rows.reduce((a, r) => addPostes(a, r.Tm), zeroPostes());
  const tauxP = rateOf(P);
  const tauxT = rateOf(T);
  return {
    n,
    nP: rows.filter((r) => r.c.p).length,
    nR: rows.filter((r) => r.c.e === 'r').length,
    nRp: rows.filter((r) => r.c.e === 'r' && r.c.p).length,
    tauxP,
    tauxT,
    dG: n ? (tauxT ?? 0) - (tauxP ?? 0) : 0,
    etat: etatOf(rows, prm),
    margeP: margOf(P),
    margeT: margOf(T),
    raf: rows.reduce((a, r) => a + r.raf, 0),
    ca: rows.reduce((a, r) => a + r.CAr, 0),
  };
}

export interface CellStat {
  cell: OpsCell;
  n: number;
  ca: number;
  /** Écart de marge à terminaison par rapport au prévu, en euros. */
  dm: number;
}

export function cellStats(rows: OpsChantier[]): CellStat[] {
  return OPS_CELLS.map((cell) => {
    const L = rows.filter((r) => cellOf(r) === cell);
    return {
      cell,
      n: L.length,
      ca: L.reduce((a, r) => a + r.CAr, 0),
      dm: L.reduce((a, r) => a + margOf(r.Tm) - margOf(r.Pv), 0),
    };
  });
}

/** Chantiers d'une case, du plus urgent au moins urgent. */
export function cellRows(rows: OpsChantier[], cell: OpsCell): OpsChantier[] {
  const L = rows.filter((r) => cellOf(r) === cell);
  if (cell.startsWith('a')) return L.sort((a, b) => b.enjS - a.enjS || b.CAr - a.CAr);
  if (cell.startsWith('g')) return L.sort((a, b) => b.CAr - a.CAr);
  return L.sort((a, b) => a.c.d - b.c.d || b.CAr - a.CAr);
}

/** Première case non vide par ordre de priorité. */
export const firstCell = (rows: OpsChantier[]): OpsCell =>
  OPS_CELLS.find((c) => rows.some((r) => cellOf(r) === c)) ?? 'r-p';

export interface OpsEntityRow {
  ent: string;
  n: number;
  r: number;
  a: number;
  g: number;
  ca: number;
  raf: number;
  tauxP: number | null;
  tauxT: number | null;
  etat: OpsHealth;
  /** Démarrages dans l'horizon (60 jours). */
  starts: number;
}

export function entityTable(rows: OpsChantier[], asOf: string, prm: OpsParams = OPS_PRM): OpsEntityRow[] {
  const by = new Map<string, OpsChantier[]>();
  for (const r of rows) by.set(r.ent, [...(by.get(r.ent) ?? []), r]);
  return [...by.entries()]
    .map(([ent, L]) => {
      const s = summarize(L, prm);
      return {
        ent,
        n: s.n,
        r: L.filter((r) => r.c.e === 'r').length,
        a: L.filter((r) => r.c.e === 'a').length,
        g: L.filter((r) => r.c.e === 'g').length,
        ca: s.ca,
        raf: s.raf,
        tauxP: s.tauxP,
        tauxT: s.tauxT,
        etat: s.etat,
        starts: L.filter((r) => r.deb && r.deb > asOf && r.deb <= addDays(asOf, prm.hz)).length,
      };
    })
    .sort((a, b) => b.ca - a.ca);
}

/** Phrase d'état de l'entreprise (ou du périmètre). */
export function headline(s: OpsSummary, subject: string): string {
  const pls = (n: number, w: string) => `${n} ${w}${n > 1 ? 's' : ''}`;
  const dGt = String(Math.abs(Math.round(s.dG * 10) / 10)).replace('.', ',');
  if (!s.n) return 'Aucun chantier en cours pour cette sélection.';
  if (s.etat === 'bon') return `${subject} va bien : marge tenue, aucun chantier en dérive.`;
  if (s.etat === 'surv') {
    if (s.nRp)
      return `${subject} tient. ${s.nRp > 1 ? `${s.nRp} chantiers dérivent mais restent pilotables` : '1 chantier dérive mais reste pilotable'} : c’est là qu’il faut agir.`;
    if (s.nR)
      return `${subject} tient. ${pls(s.nR, 'chantier')} en dérive ${s.nR > 1 ? 'sont' : 'est'} en fin de chantier : à constater et à retenir pour les prochains chiffrages.`;
    return `${subject} tient, mais la marge recule de ${dGt} pt par rapport au prévu.`;
  }
  return `Alerte : ${pls(s.nR, 'chantier')} en dérive, la marge ${s.dG < 0 ? `recule de ${dGt} pt` : 'tient encore'}.${
    s.nRp
      ? ` ${s.nRp} ${s.nRp > 1 ? 'sont' : 'est'} encore pilotable${s.nRp > 1 ? 's' : ''} : priorité absolue.`
      : ''
  }`;
}

/** Restreint le jeu de données aux entités visibles (null = toutes). */
export function restrictOps(d: OpsDataset, visible: string[] | null): OpsDataset {
  if (!visible) return d;
  const ok = (e: string) => visible.includes(e);
  return {
    ...d,
    rows: d.rows.filter((r) => ok(r.ent)),
    internes: d.internes.filter((r) => ok(r.ent)),
    ents: Object.fromEntries(Object.entries(d.ents).filter(([k]) => ok(k))),
    n1: d.n1?.filter((r) => ok(r.ent)),
  };
}
